import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type Locale = "en" | "es" | "fr" | "de" | "pt" | "ja" | "it" | "nl";

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  ja: "日本語",
  it: "Italiano",
  nl: "Nederlands",
};

export interface UserLocale {
  user_id: string;
  locale: Locale;
  auto_translate_incoming: boolean;
  updated_at: string;
}

export function useUserLocale() {
  const { user } = useAuth();
  const [row, setRow] = useState<UserLocale | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("user_locale")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setRow(
      (data as UserLocale | null) ?? {
        user_id: user.id,
        locale: "en",
        auto_translate_incoming: false,
        updated_at: new Date().toISOString(),
      },
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const update = useCallback(
    async (patch: Partial<Pick<UserLocale, "locale" | "auto_translate_incoming">>) => {
      if (!user) return { error: "not authenticated" };
      const { error } = await supabase.from("user_locale").upsert({
        user_id: user.id,
        locale: row?.locale ?? "en",
        auto_translate_incoming: row?.auto_translate_incoming ?? false,
        ...patch,
        updated_at: new Date().toISOString(),
      });
      if (!error) await refresh();
      return { error: error?.message ?? null };
    },
    [user, row, refresh],
  );

  return { locale: row?.locale ?? "en", autoTranslate: row?.auto_translate_incoming ?? false, loading, update, refresh };
}
